import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Tooltip
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import Box from "@mui/material/Box";
import {useCallback, useEffect, useRef, useState} from "react";
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import './styles/chat.css'
import TaskStepSingle from "./taskStepSingle";
import StepTitleBlock from "./stepTitleBlock";
import TaskStep from "./taskStep";
import {CommandType, currentTaskCacheProps, TaskStatusType} from "../../types/taskFlow.types";
import {useTaskFlow} from "../../context/TaskFlowContext";
import {useSocketCommands} from "../../hooks/useSocketCommands";
import {AddIcon} from "../../icons";
import uploadTips from "../../assets/uploadTips.png"
import CancelIcon from '@mui/icons-material/Cancel';
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../store";
import {setSelectCanvasImage} from "../../store/taskSlice";
import {uploadLocalImage} from "../../api/common";
import {getLocalConversionId, getLocalStorage} from "../../util/storage";
import {useCanvasControl} from "../../hooks/useCanvasControl";
import {setScreenPosition} from "../../store/drawSlice";
import ToolBar from "../tool_bar";
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom';

export default function HangingChat(props: {controlHistory: (id: number)=> void}) {

    const { controlHistory } = props;

    const dispatch = useDispatch();

    const { loading, snackbarOpen, taskProcess, handleSetPrompt, editor} = useTaskFlow()

    const { t } = useTranslation()

    const {getSelectFrameShape} = useCanvasControl(editor)

    const { getConversionId, analysis, executeTask } = useSocketCommands();

    const [tooltipOpen, setTooltipOpen] = useState(false);

    const parentStepId = useSelector((state: RootState) => state.draw.currentStepId)

    const [currentTaskCacheValue, setCurrentTaskCacheValue] = useState<currentTaskCacheProps | null>(null);

    const [openStep, setOpenStep] = useState(false);

    const [message, setMessage] = useState("");

    const [messages, setMessages] = useState<Array<{text: string, isUser: boolean}>>([]);

    const [showAddImageBox, setShowAddImageBox] = useState(false);

    const [openUploadTips, setOpenUploadTips] = useState(false);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const boardId = queryParams.get('boardId');

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [uploadImages, setUploadImages] = useState<string[]>([])

    const selectCanvasImage = useSelector((state: RootState) => state.task.canvasImage);

    const screenPosition = useSelector((state: RootState) => state.draw.screenPosition);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            try {
                const response = await uploadLocalImage('current-client-id', file);
                if (response) {
                    const url = (response as any).lod0
                    dispatch(setSelectCanvasImage(url))
                }

                setShowAddImageBox(false);
            } catch (error) {
                console.error(t('upload_failed'), error);
            }
        }
    };

    const handleLocalUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleClickOpen = () => {
        setOpenUploadTips(true);
    };

    const handleClose = () => {
        setOpenUploadTips(false);
    };

    const handleTooltipOpen = () => {
        setTooltipOpen(true);
    };

    const handleTooltipClose = () => {
        setTooltipOpen(false);
    };

    const execute = useCallback(()=>{
        return (currentTaskCacheValue && currentTaskCacheValue?.icon !== TaskStatusType.CANCELED && currentTaskCacheValue?.icon !== TaskStatusType.EXCEPTION)
    },[currentTaskCacheValue]);

    useEffect(() => {
        const analysisTask = taskProcess?.filter(item=>item.triggerCmd === CommandType.ANALYSIS);

        const parserTaskCardTask = taskProcess?.filter(
            item=>item.triggerCmd === CommandType.PARSER_TASK_CARD && item.status === TaskStatusType.EXECUTING
        );

        const chatTaskCardTask = taskProcess?.filter(
            item=>item.triggerCmd === CommandType.CHAT && item.status === TaskStatusType.EXECUTING
        );

        if ((parserTaskCardTask && parserTaskCardTask.length) || (chatTaskCardTask && chatTaskCardTask.length)) {
            let content = '';
            if (parserTaskCardTask?.length) {
                content = parserTaskCardTask[0].content || ''
            }
            if (chatTaskCardTask?.length) {
                content = chatTaskCardTask[0].content || ''
            }
            const currentTask = {
                icon: TaskStatusType.EXECUTING,
                color: 'var(--c-accent-bg)',
                template: <></>,
                task: {
                    toolName: '',
                    note: t('parsing_task'),
                    parameters: null
                },
                index: 0,
                content,
                count: 0
            }
            setCurrentTaskCacheValue(currentTask);
        }else if (analysisTask && analysisTask.length) {
            const lastAnalysisTask = analysisTask[analysisTask.length - 1];
            const status = JSON.parse(lastAnalysisTask.status as string);
            if (status.selfStatus === TaskStatusType.CANCELED || status.selfStatus === TaskStatusType.EXCEPTION || status.selfStatus === TaskStatusType.COMPLETED) {
                setCurrentTaskCacheValue(null);
            }
        }else if (!loading || snackbarOpen) {
            setCurrentTaskCacheValue(null);
        }
    }, [loading, snackbarOpen, taskProcess]);

    const touchStep = (open: boolean) => {
        setOpenStep(open)
    }

    const handleSend = () => {
        if (message.trim() || selectCanvasImage?.length) {
            handleSetPrompt(message)
            setMessages([...messages, { text: message, isUser: true }]);
            setMessage("");

            const conversionId = getLocalConversionId(boardId as string);

            if (conversionId) {
                const imageUuid = selectCanvasImage?.map(src => {
                    return src.split('/').reverse()[0]
                }) ?? []
                analysis(conversionId, message, imageUuid, parentStepId);
                dispatch(setSelectCanvasImage(null))
            }else {
                getConversionId(message);
            }

            const currentTask = {
                icon: TaskStatusType.EXECUTING,
                color: 'var(--c-accent-bg)',
                template: <></>,
                task: {
                    toolName: '',
                    note: t('parsing_task'),
                    parameters: null
                },
                index: 0,
                content: message,
                count: 0
            }
            setCurrentTaskCacheValue(currentTask);

            editor?.selectNone();
            dispatch(setScreenPosition(null))

            //  handleSetTaskProcess({content: message, triggerCmd: CommandType.SEND_PROMPT, status: TaskStatusType.EXECUTING, response: {prompt: message}})
            /*       if (conversionId) {
                       analysis(conversionId, message)
                   }else {
                       getConversionId(message);
                   }*/
        }
    };

    const inputRef = useRef<HTMLDivElement>(null);

    const taskBlockRef = useRef<HTMLDivElement>(null);
    const uploadBlockRef = useRef<HTMLDivElement>(null);

    const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
            const textareaElement = inputRef.current?.querySelector('textarea');
            textareaElement?.blur();
        }
    }, [handleSend]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openStep && taskBlockRef.current && !taskBlockRef.current.contains(event.target as Node)) {
                setOpenStep(false);
            }
            if (showAddImageBox && uploadBlockRef.current && !uploadBlockRef.current.contains(event.target as Node)) {
                setShowAddImageBox(false)
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openStep, showAddImageBox]);

    const backgroundColor = () => {
        return 'var(--c-accent-bg)'
    }

    const currentTaskCache = useCallback((value: currentTaskCacheProps) => {
        if (value?.status !== currentTaskCacheValue?.status || value?.icon !== currentTaskCacheValue?.icon) setCurrentTaskCacheValue(value);
    }, [currentTaskCacheValue]); // 依赖当前值

    return         <Box>
        <ToolBar editor={editor} controlHistory={controlHistory} loading={loading}/>
    </Box>  /*<>
        <Dialog
            open={openUploadTips}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogContent style={{textAlign: "center"}}>
                <DialogContentText id="alert-dialog-description">
                    <span style={{color: 'var(--c-text-primary)', fontSize: 14}}>在画板中选中图片，可点击“添加到对话”按钮或“Enter”键将其添加到聊天框内</span>
                </DialogContentText>
                <img src={uploadTips} style={{width: "80%", marginTop: 20}} alt=""/>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} autoFocus>
                    OK
                </Button>
            </DialogActions>
        </Dialog>
        <div
            ref={taskBlockRef}
            className="bounce-animation"
            style={{
                display: openStep ? 'block' : 'none',
                animation: openStep ? 'bounce 0.5s ease' : 'none'
            }}
        >
            <TaskStep currentTaskCache={currentTaskCache} />
        </div>
        { !openStep && execute() && <StepTitleBlock backgroundColor={backgroundColor()}  currentTaskCacheValue={currentTaskCacheValue}/>}
        <Box sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            left: 8,
            backgroundColor: 'transparent',
            borderRadius: '12px',
            display: "flex",
            alignItems: "center",
        }}>
            {!execute() && <div className='imagePreview'>
                {
                    selectCanvasImage?.map((item, index) => {
                        return (
                            <div key={index} style={{position: 'relative', width: '60px', marginLeft: "8px"}}>
                                <div className='removeImage' onClick={()=>{
                                    dispatch(setSelectCanvasImage(null))
                                }}><CancelIcon style={{fontSize: 18}}/></div>
                                <img src={`${process.env.REACT_APP_PROXY_TARGET}${item}`}
                                     width='60'
                                     height='60' style={{objectFit: 'cover'}} alt=''/>
                            </div>
                        )
                    })
                }
            </div>}
            {execute() && <TaskStepSingle touchStep={touchStep} currentTaskCacheValue={currentTaskCacheValue}
                                          openStep={openStep} backgroundColor={backgroundColor()} /> }
            { !execute() && <FormControl className='chatInput' variant="outlined">
                <InputLabel htmlFor="outlined-adornment-password">请输入指令</InputLabel>
                <Tooltip
                    open={tooltipOpen}
                    disableHoverListener
                    disableFocusListener
                    onClose={handleTooltipClose}
                    onOpen={handleTooltipOpen}
                    title="当前还有任务在执行"
                    placement="top-start"
                    arrow
                >
                    <OutlinedInput
                        ref={inputRef}
                        id="outlined-adornment-password"
                        type='text'
                        style={{borderRadius: 12, background: getSelectFrameShape(parentStepId) ? 'var(--c-border-light)' : 'none'}}
                        multiline
                        maxRows={4}
                        placeholder="输入消息..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        fullWidth
                        disabled={!!getSelectFrameShape(parentStepId)}
                        size="small"
                        endAdornment={
                            <InputAdornment position="end">
                                <div style={{cursor: 'pointer', marginTop: 7}}
                                     onClick={() => {
                                         setShowAddImageBox(true)
                                     }}><AddIcon/></div>
                                <IconButton
                                    aria-label='hide the password'
                                    edge="end"
                                >
                                    <SendIcon style={{fontSize: 20, color: 'var(--c-accent)'}} onClick={handleSend}/>
                                </IconButton>
                            </InputAdornment>
                        }
                        label="Password"
                    />
                </Tooltip>
                {
                    showAddImageBox && <div className="addImagBox" ref={uploadBlockRef}>
                        <div onClick={() => {
                            setOpenUploadTips(true)
                        }}>从画板选择
                        </div>
                        <div onClick={handleLocalUploadClick}>从本地上传</div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept="image/!*"
                            style={{display: 'none'}}
                        />
                    </div>
                }
            </FormControl>}
            <div className='chatHistoryIcon' onClick={()=>{controlHistory(1)}}><ContentPasteSearchIcon style={{color: 'var(--c-text-primary)'}} /></div>
        </Box>
    </>*/
}